/**
 * IL PREVENTIVO IN BACHECA — sezione `board` del suo `EntityTypeDescriptor` (TS-117).
 *
 * È il wiring che stava dentro `KanbanBoardPage`, impacchettato qui **senza cambiarlo**: la board
 * dei preventivi deve continuare a funzionare esattamente come prima, ed è il criterio di
 * accettazione che conta di più dell'intera issue.
 *
 * Le card si caricano dal REST del *preventivo* con `?kanbanId=`, non da un endpoint neutro: è il
 * server a risolvere il filtro della board, e il DTO che torna è quello pieno del preventivo, così
 * la card mantiene importo, azienda e scadenza invece di un minimo comune denominatore.
 *
 * `quotationApi` si importa **dinamicamente**, come fanno `resolveLabel` e `search` nel registro:
 * il modulo api non deve finire nel percorso di boot, altrimenti si riapre il ciclo di import che
 * `coreEntityRegistration` evita per costruzione. La card invece è un import statico: disegna e
 * basta, e non torna indietro al registro.
 */

import type { EntityTypeDescriptor, Page } from '@crm/contracts';
import { QuotationKanbanCard } from './QuotationKanbanCard';
import type { Quotation } from './quotationTypes';

type BoardSection = NonNullable<EntityTypeDescriptor['board']>;

/** Il descrittore parla di record generici; qui sotto sono preventivi, e il cast sta tutto qui. */
const asQuotation = (item: Record<string, unknown>) => item as unknown as Quotation;

export const quotationBoard: BoardSection = {
  // pageSize alto e non paginazione: la board mostra tutte le colonne insieme, e una card mancante
  // non si distingue da una card filtrata via.
  loadItems: async (kanbanId, groupId) => {
    const { quotationApi } = await import('./quotationApi');
    const page = await quotationApi.getAll(groupId, { kanbanId, pageSize: 1000 });

    return page as unknown as Page<Record<string, unknown>>;
  },

  idOf: (item) => asQuotation(item).quotationId,

  stateIdOf: (item) => asQuotation(item).currentStateId ?? 0,

  move: async (id, pipelineStateId) => {
    const { quotationApi } = await import('./quotationApi');

    await quotationApi.update({ quotationId: id, currentStateId: pipelineStateId });
  },

  Card: ({ item }) => <QuotationKanbanCard quotation={asQuotation(item)} />,
};
